import { Link } from "react-router-dom";
import {
    Users,
    Globe,
    Zap,
    TrendingUp,
    Sparkles,
    ArrowRight,
    Target,
    Rocket,
    Heart,
    Shield,
    Play,
    Award,
    Star,
    MonitorSmartphone,
    BadgeCheck,
    Video,
    Palette,
    BarChart3,
} from "lucide-react";
import BackToTop from "../components/BackToTop/BackToTop";
import { useEffect } from "react";
import Footer from "../components/Footer";
import Header from "../components/Header";

const About = () => {

    // Platform features
    const features = [
        {
            icon: Video,
            title: "Social to TV Conversion",
            description: "Transform Instagram Reels, TikTok videos, and YouTube content into TV-ready ads automatically.",
            color: "from-blue-500 to-cyan-500",
            stats: "3x faster creation"
        },
        {
            icon: MonitorSmartphone,
            title: "Multi-Platform Delivery",
            description: "Deploy campaigns across Roku, Amazon Fire TV, Samsung TV, and Apple TV with one click.",
            color: "from-purple-500 to-pink-500",
            stats: "10+ platforms"
        },
        {
            icon: BarChart3,
            title: "Real-Time Analytics",
            description: "Track performance across all channels with unified dashboard and actionable insights.",
            color: "from-green-500 to-emerald-500",
            stats: "Live reporting"
        },
        {
            icon: Palette,
            title: "Creative Optimization",
            description: "AI-powered tools to enhance and adapt your social content for the big screen.",
            color: "from-orange-500 to-red-500",
            stats: "4K quality"
        }
    ];

    // Statistics with icons
    const stats = [
        { value: "10K+", label: "Ads Generated", icon: Play, change: "+156% YoY" },
        { value: "4K+", label: "Active Brands", icon: Users, change: "+89% YoY" },
        { value: "120+", label: "Countries", icon: Globe, change: "Global reach" },
        { value: "98%", label: "Satisfaction", icon: Star, change: "Top rated" }
    ];

    // Values with descriptions
    const values = [
        {
            icon: Rocket,
            title: "Innovation First",
            description: "We're constantly pushing the boundaries of what's possible in advertising technology.",
            color: "bg-blue-50",
            iconColor: "text-blue-600"
        },
        {
            icon: Heart,
            title: "Customer Obsessed",
            description: "Your success is our success. We build features that solve real problems.",
            color: "bg-red-50",
            iconColor: "text-red-600"
        },
        {
            icon: Shield,
            title: "Trust & Transparency",
            description: "Clear pricing, honest communication, and data privacy are our foundations.",
            color: "bg-green-50",
            iconColor: "text-green-600"
        },
        {
            icon: Target,
            title: "Results Driven",
            description: "Every feature is measured by the impact it creates for our customers.",
            color: "bg-purple-50",
            iconColor: "text-purple-600"
        }
    ];


    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="bg-gradient-to-b from-gray-50 to-white">
            <BackToTop />

            {/* ================= HEADER ================= */}
            <Header />


            <div className="relative">
                <div className="absolute inset-0 overflow-hidden">
                    {/* Gradient orbs with modern colors */}
                    <div className="absolute bottom-0 -right-40 w-[400px] h-[400px] bg-gradient-to-l from-pink-200/20 via-purple-200/20 to-indigo-200/20 rounded-full blur-3xl animate-float-slower"></div>
                    <div className="absolute top-0 -left-40 w-[400px] h-[400px] bg-gradient-to-r from-indigo-200/20 via-purple-200/20 to-pink-200/20 rounded-full blur-3xl animate-float-slow"></div>

                    {/* Floating particles - Minimal */}
                    {[...Array(50)].map((_, i) => (
                        <div
                            key={i}
                            className="absolute w-1 h-1 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-full animate-float-particle"
                            style={{
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`,
                                animationDelay: `${Math.random() * 8}s`,
                                animationDuration: `${15 + Math.random() * 15}s`,
                                opacity: 0.3,
                            }}
                        />
                    ))}
                </div>


                <div className="relative">
                    {/* Hero Section with animated gradient */}
                    <section className="relative max-w-7xl mx-auto px-6 py-20 overflow-hidden">
                        {/* Animated background */}
                        <div className="absolute inset-0 -z-10">
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-indigo-600/5 to-purple-600/5 rounded-full blur-3xl animate-pulse"></div>
                            <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-blue-600/5 rounded-full blur-3xl"></div>
                            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-600/5 rounded-full blur-3xl"></div>
                        </div>

                        <div className="text-center max-w-4xl mx-auto">
                            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-50 to-purple-50 px-4 py-2 rounded-full mb-8 border border-indigo-100">
                                <Sparkles className="h-4 w-4 text-indigo-600" />
                                <span className="text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                    About SocialTV
                                </span>
                            </div>

                            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8 leading-tight">
                                Turning Social Content Into{' '}
                                <span className="relative">
                                    <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                                        TV Ads
                                    </span>
                                    <div className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-full"></div>
                                </span>
                            </h1>

                            {/* <h1 className="text-5xl md:text-6xl font-bold mb-4">
                        Turning Social Content Into{' '}
                        <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                            TV Ads
                        </span>
                    </h1> */}

                            <p className="text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed">
                                SocialTV helps brands transform their social media videos into
                                powerful Connected TV ads in minutes. Our platform bridges the gap
                                between social content and premium TV advertising.
                            </p>

                            {/* Trust indicators */}
                            {/* <div className="flex items-center justify-center gap-8 mt-12">
                        <div className="flex items-center gap-2">
                            <div className="flex -space-x-2">
                                {[1, 2, 3, 4].map((i) => (
                                    <img
                                        key={i}
                                        src={`https://images.unsplash.com/photo-${i === 1 ? '1494790108777-466d103eb9cb' : i === 2 ? '1507003211169-0a1dd7228f2d' : i === 3 ? '1438761681033-6461ffad8d80' : '1500648767791-00dcc994a37e'}?ixlib=rb-4.0.3&auto=format&fit=crop&w=32&h=32&q=80`}
                                        className="w-8 h-8 rounded-full border-2 border-white"
                                        alt="User"
                                    />
                                ))}
                            </div>
                            <span className="text-sm text-gray-500">Trusted by 4,000+ brands</span>
                        </div>
                        <div className="h-4 w-px bg-gray-200"></div>
                        <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                            <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                            <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                            <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                            <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                            <span className="text-sm text-gray-500 ml-1">4.9/5 rating</span>
                        </div>
                    </div> */}
                        </div>
                    </section>

                    {/* Stats Section with hover effects */}
                    <section className="max-w-7xl mx-auto px-6 pb-24">
                        <div className="grid md:grid-cols-4 gap-6">
                            {stats.map((stat, i) => (
                                <div
                                    key={i}
                                    className="group relative bg-white p-8 rounded-2xl border border-gray-200 hover:border-indigo-600/20 hover:shadow-xl transition-all hover:-translate-y-1"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 to-purple-600/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>

                                    <div className="relative">
                                        <div className="w-12 h-12 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                            <stat.icon className="h-6 w-6 text-indigo-600" />
                                        </div>

                                        <h3 className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</h3>
                                        <p className="text-sm text-gray-500 mb-3">{stat.label}</p>

                                        <div className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                            <TrendingUp className="h-3 w-3" />
                                            {stat.change}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Story Section with image */}
                    <section className="max-w-7xl mx-auto px-6 pb-24">
                        <div className="grid lg:grid-cols-2 gap-16 items-center">
                            <div className="relative">
                                <div className="absolute -inset-4 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl blur-xl opacity-20"></div>
                                <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
                                    <img
                                        src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                                        alt="Team collaboration"
                                        className="w-full h-96 object-cover"
                                    />
                                    {/* <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                                <div className="flex items-center gap-4">
                                    <PlayCircle className="h-10 w-10 text-indigo-600" />
                                    <div>
                                        <div className="font-medium text-gray-900">Watch our story</div>
                                        <div className="text-sm text-gray-500">2 min • Behind the scenes</div>
                                    </div>
                                </div>
                            </div> */}
                                </div>
                            </div>

                            <div>
                                <div className="inline-flex items-center gap-2 bg-indigo-50 px-4 py-2 rounded-full mb-6">
                                    <Target className="h-4 w-4 text-indigo-600" />
                                    <span className="text-sm font-medium text-indigo-600">Our Mission</span>
                                </div>

                                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                                    Bridging Social &{' '}
                                    <span className="text-indigo-600">Television</span>
                                </h2>

                                <div className="space-y-4 text-gray-600">
                                    <p className="leading-relaxed">
                                        Advertising is evolving. Social media has become the most powerful
                                        form of storytelling, but television remains the most trusted
                                        screen in the home.
                                    </p>

                                    <p className="leading-relaxed">
                                        SocialTV was created to connect these two worlds — enabling
                                        businesses to easily transform social content into high-quality
                                        TV-ready ads.
                                    </p>

                                    <p className="leading-relaxed">
                                        Our platform simplifies the entire workflow, allowing brands,
                                        agencies, and creators to launch TV campaigns faster than ever
                                        before.
                                    </p>
                                </div>

                                {/* Company highlights */}
                                <div className="grid grid-cols-2 gap-4 mt-8">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                                            <BadgeCheck className="h-5 w-5 text-green-600" />
                                        </div>
                                        <div>
                                            <div className="font-medium text-gray-900">ISO Certified</div>
                                            <div className="text-xs text-gray-500">Security & privacy</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                                            <Award className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <div className="font-medium text-gray-900">Award Winning</div>
                                            <div className="text-xs text-gray-500">Best AdTech 2024</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Features Grid */}
                    <section className="max-w-7xl mx-auto px-6 pb-24">
                        <div className="text-center max-w-2xl mx-auto mb-16">
                            <div className="inline-flex items-center gap-2 bg-indigo-50 px-4 py-2 rounded-full mb-4">
                                <Zap className="h-4 w-4 text-indigo-600" />
                                <span className="text-sm font-medium text-indigo-600">Platform Features</span>
                            </div>
                            <h2 className="text-4xl font-bold text-gray-900 mb-4">
                                Everything You Need to Succeed
                            </h2>
                            <p className="text-lg text-gray-500">
                                Powerful tools that make TV advertising accessible to everyone
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {features.map((feature, i) => (
                                <div
                                    key={i}
                                    className="group relative bg-white p-8 rounded-2xl border border-gray-100 hover:border-transparent hover:shadow-xl transition-all hover:-translate-y-1"
                                >
                                    <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} rounded-2xl opacity-0 group-hover:opacity-5 transition-opacity`}></div>

                                    <div className="relative">
                                        <div className={`w-14 h-14 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                                            <feature.icon className="h-7 w-7 text-white" />
                                        </div>

                                        <h3 className="text-lg font-semibold text-gray-900 mb-3">{feature.title}</h3>
                                        <p className="text-sm text-gray-500 leading-relaxed mb-4">{feature.description}</p>

                                        <div className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full">
                                            {feature.stats}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Values Section */}
                    <section className="max-w-7xl mx-auto px-6 pb-24">
                        <div className="text-center max-w-2xl mx-auto mb-16">
                            <div className="inline-flex items-center gap-2 bg-indigo-50 px-4 py-2 rounded-full mb-4">
                                <Heart className="h-4 w-4 text-indigo-600" />
                                <span className="text-sm font-medium text-indigo-600">Our Values</span>
                            </div>
                            <h2 className="text-4xl font-bold text-gray-900 mb-4">
                                What Drives Us Forward
                            </h2>
                            <p className="text-lg text-gray-500">
                                Principles that guide every decision we make
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {values.map((value, i) => (
                                <div key={i} className="group p-8 rounded-2xl border border-gray-100 hover:border-gray-200 transition-all">
                                    <div className={`w-14 h-14 ${value.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                                        <value.icon className={`h-7 w-7 ${value.iconColor}`} />
                                    </div>

                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{value.title}</h3>
                                    <p className="text-sm text-gray-500 leading-relaxed">{value.description}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* CTA Section with gradient */}
                    <section className="max-w-7xl mx-auto px-6 pb-28">
                        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white p-16 text-center shadow-2xl">

                            <div className="relative">
                                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                                    Start Creating CTV Ads Today
                                </h2>

                                <p className="text-indigo-100 text-lg mb-8 max-w-2xl mx-auto">
                                    Turn your social videos into powerful TV ads in minutes. Join thousands of brands already using SocialTV.
                                </p>

                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <Link
                                        to="/dashboard"
                                        className="inline-flex items-center justify-center gap-2 bg-white text-indigo-600 px-8 py-4 rounded-xl font-medium hover:bg-gray-100 transition-all hover:scale-105 active:scale-95 shadow-lg"
                                    >
                                        Start Free Trial
                                        <ArrowRight className="h-4 w-4" />
                                    </Link>

                                    <Link
                                        to="/contact"
                                        className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-xl font-medium hover:bg-white/20 transition-all"
                                    >
                                        Contact Sales
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>


            {/* ================= FOOTER ================= */}
            <Footer />

        </div>
    );
};

export default About;